"use client";

import type React from "react";
import {
  AccessPointsVisual,
  AccountManagerVisual,
  ApiSdkVisual,
  CertificationBadgesVisual,
  DashboardPreviewVisual,
  DatacenterSpeedVisual,
  GeoTargetVisual,
  IpPoolVisual,
  ProtocolsVisual,
  RedundantRoutingVisual,
  RotationVisual,
  SetupStepsVisual,
  TeamClockVisual,
  TransparentPricingVisual,
} from "@/components/sections/products/shared/feature-visuals";
import {
  ConsentTogglesVisual,
  KycScanVisual,
  SubUsersVisual,
  SupportChatVisual,
} from "@/components/sections/home/why-proxidize/small-feature-visuals";

export interface EnterpriseItem {
  /** Ambient looping visual rendered above the card title. */
  Visual: React.ComponentType;
  title: string;
  description: string;
}

// Enterprise features: security, compliance, and account controls teams expect.
// Copy mirrors the homepage "Why Proxidize" and About pages.
export const enterpriseFeatures: EnterpriseItem[] = [
  {
    Visual: ConsentTogglesVisual,
    title: "Ethically Sourced IPs",
    description:
      "Every IP in our network is opt-in. Real users voluntarily share bandwidth with strict consent across all devices.",
  },
  {
    Visual: CertificationBadgesVisual,
    title: "SOC 2 & ISO 27001 Certified",
    description:
      "Independently attested and certified. The security and operational standards your team expects before signing.",
  },
  {
    Visual: KycScanVisual,
    title: "KYC & Compliance",
    description:
      "We require KYC (Know Your Customer) verification from all customers and maintain a strict acceptable-use policy.",
  },
  {
    Visual: SubUsersVisual,
    title: "Sub-user Management",
    description:
      "Create sub-users on the account level or access points/proxy users to separate proxy usage.",
  },
  {
    Visual: DashboardPreviewVisual,
    title: "Real-time Dashboard",
    description:
      "Monitor bandwidth usage, success rates, active sessions, and billing, all in one clean, live dashboard.",
  },
  {
    Visual: TransparentPricingVisual,
    title: "Transparent Pricing",
    description: "Simple per-GB and per-proxy plans with no commitments and no contracts.",
  },
];

// Dedicated infrastructure — network reach we own and operate end to end.
export const infrastructureItems: EnterpriseItem[] = [
  {
    Visual: GeoTargetVisual,
    title: "195+ Countries",
    description:
      "Residential IPs across 195+ countries with city and ISP-level targeting. Mobile IPs across dozens of locations worldwide with city and ASN/ISP precision.",
  },
  {
    Visual: IpPoolVisual,
    title: "Millions of Real IPs",
    description: "Ethically sourced residential and mobile IPs.",
  },
  {
    Visual: RedundantRoutingVisual,
    title: "Redundant Routing",
    description:
      "Enterprise-grade infrastructure with redundant routing means your pipelines never go dark.",
  },
  {
    Visual: DatacenterSpeedVisual,
    title: "Datacenter Speeds",
    description: "1 Gbps speeds with 99.9% target uptime.",
  },
];

// Custom deployments — API-first provisioning that fits any stack.
export const deploymentItems: EnterpriseItem[] = [
  {
    Visual: AccessPointsVisual,
    title: "Fast Provisioning",
    description:
      "Deploy a proxy pool in minutes, not days. Our API-first architecture means no waiting for manual setup.",
  },
  {
    Visual: ProtocolsVisual,
    title: "All Protocols",
    description: "HTTP, HTTPS, and SOCKS5 proxies. Whatever your stack uses, we support.",
  },
  {
    Visual: RotationVisual,
    title: "Smart Rotation",
    description: "Auto-rotate IPs on every request, or pin a session.",
  },
  {
    Visual: ApiSdkVisual,
    title: "REST API & SDK",
    description: "Use the REST API and TypeScript SDK to extend Proxidize.",
  },
  {
    Visual: SubUsersVisual,
    title: "Sub-user Management",
    description:
      "Create sub-users on the account level or access points/proxy users to separate proxy usage.",
  },
  {
    Visual: SetupStepsVisual,
    title: "5-minute Setup",
    description: "One endpoint, one credential pair. Works with every HTTP client.",
  },
];

// Priority support — fragments of the "Dedicated Support" copy from the homepage
// and About page, split into distinct support guarantees.
export const supportItems: EnterpriseItem[] = [
  {
    Visual: SupportChatVisual,
    title: "Live Chat & Email",
    description: "Premium support via live chat and email, included on every plan.",
  },
  {
    Visual: AccountManagerVisual,
    title: "Dedicated Account Manager",
    description: "Enterprise plans come with a dedicated account manager.",
  },
  {
    Visual: TeamClockVisual,
    title: "Around the Clock",
    description:
      "Real engineers, not bots, available around the clock to solve your most complex proxy challenges.",
  },
];
